import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import HomePage from './pages/HomePage';
import { testSupabaseConnection, initializeDatabase } from './utils/testSupabase';
import './index.css';

// Lazy load components for better performance
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/NewRegisterPage')); // NEW SYSTEM
const CompleteRegisterPage = lazy(() => import('./pages/CompleteRegisterPage')); // COMPLETE REGISTRATION FORM
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ProviderResetPassword = lazy(() => import('./pages/ProviderResetPassword'));
const ProviderDashboard = lazy(() => import('./pages/NewProviderDashboard')); // NEW SYSTEM
const ServicePage = lazy(() => import('./pages/NewServicePage')); // NEW SYSTEM
const ProviderDetailPage = lazy(() => import('./pages/ProviderDetailPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const ClientProfilePage = lazy(() => import('./pages/ClientProfilePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ClientOrdersPage = lazy(() => import('./pages/ClientOrdersPage'));
const ClientMissionsPage = lazy(() => import('./pages/ClientMissionsPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminRegisterPage = lazy(() => import('./pages/AdminRegisterPage'));
const AdminForgotPasswordPage = lazy(() => import('./pages/AdminForgotPasswordPage'));
const AdminResetPasswordPage = lazy(() => import('./pages/AdminResetPasswordPage'));
const AdminDashboard = lazy(() => import('./pages/NewAdminDashboard')); // NEW SYSTEM
const AdminConfigurationPage = lazy(() => import('./pages/AdminConfigurationPage'));
const AdminPaymentConfigPage = lazy(() => import('./pages/AdminPaymentConfigPage'));
const AccountBlockedPage = lazy(() => import('./pages/AccountBlockedPage'));
const ProviderSubscriptionRenewalPage = lazy(() => import('./pages/ProviderSubscriptionRenewalPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Chargement...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    // Hide SEO fallback content once React has mounted
    const seoFallback = document.getElementById('seo-fallback');
    if (seoFallback) {
      seoFallback.style.display = 'none';
    }
    
    // Test Supabase connection on app start (non-blocking)
    const testConnection = async () => {
      try {
        const isConnected = await testSupabaseConnection();
        if (isConnected) {
          await initializeDatabase();
        }
      } catch (error) {
        console.warn('Database connection failed, but app will continue to work:', error);
      }
    };
    
    // Run connection test without blocking the app
    testConnection();
  }, []);

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-gray-50 flex flex-col">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin Routes - No Header/Footer */}
                <Route path="/adminmrg2025/login" element={<AdminLoginPage />} />
                <Route path="/adminmrg2025/register" element={<AdminRegisterPage />} />
                <Route path="/adminmrg2025/forgot-password" element={<AdminForgotPasswordPage />} />
                <Route path="/adminmrg2025/reset-password" element={<AdminResetPasswordPage />} />
                <Route path="/adminmrg2025/dashboard" element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                } />
                <Route path="/adminmrg2025/configuration" element={
                  <AdminProtectedRoute>
                    <AdminConfigurationPage />
                  </AdminProtectedRoute>
                } />
                <Route path="/adminmrg2025/payment-config" element={
                  <AdminProtectedRoute>
                    <AdminPaymentConfigPage />
                  </AdminProtectedRoute>
                } />
                <Route path="/account-blocked" element={<AccountBlockedPage />} />
                
                {/* OAuth Callback Route - No Header/Footer */}
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                
                {/* Regular Routes with Header/Footer */}
                <Route path="/*" element={
                  <>
                    <Header />
                    <main className="flex-1">
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        
                        {/* Routes d'authentification - redirigent vers dashboard si déjà connecté */}
                        <Route path="/login" element={
                          <ProtectedRoute requireAuth={false}>
                            <LoginPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/register" element={
                          <ProtectedRoute requireAuth={false}>
                            <RegisterPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/complete-register" element={
                          <ProtectedRoute requireAuth={false}>
                            <CompleteRegisterPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/reset-password" element={
                          <ProtectedRoute requireAuth={false}>
                            <ResetPasswordPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/provider-reset-password" element={
                          <ProtectedRoute requireAuth={false}>
                            <ProviderResetPassword />
                          </ProtectedRoute>
                        } />
                        
                        {/* Routes protégées - nécessitent une authentification */}

                        <Route path="/provider-dashboard" element={
                          <ProtectedRoute requireAuth={true}>
                            <ProviderDashboard />
                          </ProtectedRoute>
                        } />

                        <Route path="/provider/subscription-renewal" element={
                          <ProtectedRoute requireAuth={true}>
                            <ProviderSubscriptionRenewalPage />
                          </ProtectedRoute>
                        } />
                        
                        {/* Routes publiques */}
                        <Route path="/service/:categorySlug" element={<ServicePage />} />
                        
                        {/* Routes SEO pour les services - redirigent vers /service/:slug */}
                        <Route path="/services/menage" element={<ServicePage />} />
                        <Route path="/services/coiffure" element={<ServicePage />} />
                        <Route path="/services/massage" element={<ServicePage />} />
                        <Route path="/services/plomberie" element={<ServicePage />} />
                        <Route path="/services/jardinage" element={<ServicePage />} />
                        <Route path="/services/cuisine" element={<ServicePage />} />
                        <Route path="/provider/:providerSlug/:categorySlug" element={<ProviderDetailPage />} />
                        <Route path="/booking/:providerId/:serviceId" element={<BookingPage />} />
                        <Route path="/client-profile" element={<ClientProfilePage />} />
                        <Route path="/client-orders" element={<ClientOrdersPage />} />
                        <Route path="/client/orders" element={<ClientMissionsPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/cookies" element={<CookiesPage />} />
                        
                        {/* Route 404 - doit être en dernier */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </main>
                    <Footer />
                    <CookieBanner />
                  </>
                } />
              </Routes>
            </Suspense>
          </div>
          <Analytics />
        </Router>
      </AdminAuthProvider>
    </AuthProvider>
  );
};

export default App;